/**
 * This module contains supporting definitions and functions for turning user-selected images into
 * the JPEG files that the app uploads. AvatarPicker (whole-image conversion), AvatarCropDialog
 * (crop-region conversion) and the IEP review’s uploads draw on the same canvas pipeline, so it lives here.
 */
const MIME_TYPE_HEIC = 'image/heic'
const MIME_TYPE_HEIF = 'image/heif'
const MIME_TYPE_JPEG = 'image/jpeg'
const MIME_TYPE_PNG = 'image/png'
const MIME_TYPE_WEBP = 'image/webp'
const HEIC_HEIF_MIME_TYPES = [MIME_TYPE_HEIC, MIME_TYPE_HEIF]
const HEIC_HEIF_EXTENSION_REGEX = /\.heic$|\.heif$/i

const JPEG_QUALITY = 0.9

// The longest edge of an exported image. Crops are never upscaled to reach it.
const DIMENSION_MAX = 800

const EXTENSION_REGEX = /\.[^/.]+$/

const DEFAULT_JPEG_NAME = 'avatar.jpg'

/**
 * Swaps whatever extension a file name carries for `.jpg`, because everything this module produces
 * is a JPEG regardless of what went in.
 *
 * @param {string} name The original file name, which may be empty.
 * @param {string} fallback The name to use when there is no original name.
 * @returns the file name with a `.jpg` extension
 */
const withJpegExtension = (name, fallback = DEFAULT_JPEG_NAME) =>
  name ? `${name.replace(EXTENSION_REGEX, '')}.jpg` : fallback

/**
 * Determines the pixel dimensions to export a crop at. Crops are scaled down to fit dimensionMax
 * but never scaled up, because an upscaled crop is just a bigger blurry file.
 *
 * @param {object} croppedAreaPixels The crop region, in natural source-image pixels.
 * @param {boolean} forceSquare True if the export should be square regardless of the region’s shape.
 * @param {number} dimensionMax The longest edge to export at; DIMENSION_MAX by default.
 * @returns an object with `width` and `height`
 */
const outputSize = (croppedAreaPixels, forceSquare, dimensionMax = DIMENSION_MAX) => {
  const cropWidth = Math.round(croppedAreaPixels.width)
  const cropHeight = Math.round(croppedAreaPixels.height)

  if (forceSquare) {
    const dimension = Math.min(dimensionMax, Math.min(cropWidth, cropHeight))
    return { width: dimension, height: dimension }
  }

  if (cropWidth <= dimensionMax && cropHeight <= dimensionMax) {
    return { width: cropWidth, height: cropHeight }
  }

  const scale = Math.min(dimensionMax / cropWidth, dimensionMax / cropHeight)
  return { width: Math.round(cropWidth * scale), height: Math.round(cropHeight * scale) }
}

/**
 * Promisifies image loading so that callers can `await` a decoded image rather than juggling
 * onload/onerror callbacks.
 *
 * @param {string} src The image source, typically an object URL.
 * @returns a promise resolving to a loaded Image
 */
const loadImage = src =>
  new Promise((resolve, reject) => {
    const image = new Image()

    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('The selected image could not be read.'))
    image.src = src
  })

/**
 * Draws a region of an image onto an off-screen canvas and encodes it as a JPEG file. Omit `source`
 * and the dimensions to convert the whole image at its natural size, scaled down to `dimensionMax`.
 *
 * @param {object} params
 * @param {string} params.imageSrc The image source, typically an object URL.
 * @param {object} params.source The region to draw, in natural source-image pixels.
 * @param {number} params.width The width to export at.
 * @param {number} params.height The height to export at.
 * @param {string} params.fileName The original file name, used to derive the JPEG’s name.
 * @param {number} params.dimensionMax The longest edge to export at when no dimensions are given.
 * @param {number} params.quality The JPEG quality, from 0 to 1.
 * @returns a promise resolving to a JPEG File
 */
const toJpegFile = async ({
  imageSrc,
  source,
  width,
  height,
  fileName,
  dimensionMax = Infinity,
  quality = JPEG_QUALITY
}) => {
  const image = await loadImage(imageSrc)
  const region = source ?? { x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight }
  const fittedSize = outputSize(region, false, dimensionMax)
  const outputWidth = width ?? fittedSize.width
  const outputHeight = height ?? fittedSize.height

  const canvas = document.createElement('canvas')
  canvas.width = outputWidth
  canvas.height = outputHeight

  const ctx = canvas.getContext('2d')

  // JPEG has no alpha channel, so anything transparent needs a matte or it encodes as black.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, outputWidth, outputHeight)
  ctx.drawImage(
    image,
    Math.round(region.x),
    Math.round(region.y),
    Math.round(region.width),
    Math.round(region.height),
    0,
    0,
    outputWidth,
    outputHeight
  )

  const blob = await new Promise(resolve => canvas.toBlob(resolve, MIME_TYPE_JPEG, quality))
  if (!blob) {
    throw new Error('The cropped image could not be saved.')
  }

  return new File([blob], withJpegExtension(fileName), { type: MIME_TYPE_JPEG })
}

/**
 * Determines whether a file looks like a HEIC or HEIF photo, either by its type or, since some browsers
 * leave the type blank for these, by its name.
 */
const isPotentialHeic = ({ type, name }) => HEIC_HEIF_MIME_TYPES.includes(type) || HEIC_HEIF_EXTENSION_REGEX.test(name)

/**
 * Converts a HEIC or HEIF photo to the given image type. Resolves to null if the file isn’t really HEIC.
 */
const convertHeic = async (file, type, quality) => {
  const { heicTo, isHeic } = await import('heic-to')
  return (await isHeic(file)) ? heicTo({ blob: file, type, quality }) : null
}

export {
  DIMENSION_MAX,
  HEIC_HEIF_EXTENSION_REGEX,
  HEIC_HEIF_MIME_TYPES,
  JPEG_QUALITY,
  MIME_TYPE_HEIC,
  MIME_TYPE_HEIF,
  MIME_TYPE_JPEG,
  MIME_TYPE_PNG,
  MIME_TYPE_WEBP,
  convertHeic,
  isPotentialHeic,
  loadImage,
  outputSize,
  toJpegFile,
  withJpegExtension
}
